import {
  ASSET_REQUEST_EMULATION_CLIENT,
  Client,
  Environment,
  getCurrentClient,
  isCurrentClient,
  isCurrentEnvironment,
  isCurrentTenant,
  MAL_EMULATION_CLIENT,
  showAdotInventoryRequests,
  Tenant,
} from './featureToggleUtilities';

// Long-lived tenant decisions grouped by area.
// Local is included by default; negating a check excludes it. Keep exact tenant lists
// and explicit local exclusions intact. Role, record, and preference checks stay at call sites.
// Usage comments name consuming components/utilities.

// ── Work and tasks ──

// Used in: TaskDetailView (work detail), TaskDetail (preview), WorkTemplateTaskDetail.
export const isTaskDescriptionEnabled = !isCurrentClient([
  Client.ALDOT,
  Client.UDOT_TMD,
  Client.MACOMB,
  Client.GDOT_SIGOPS,
]);

// Used in: WorkOrderDetailHeader, WorkOrdersTableRow, PreviewHeaderOptions.
export const isManagerWorkDeleteRoleEnabled = isCurrentClient([
  Client.HENNEPIN,
  Client.MACOMB,
]);

// Used in: WorkOrderDetailHeader.
export const isOwnWorkVersioningEnabled = isCurrentClient([Client.HENNEPIN]);

// Used in: WorkOrderStatusEdit.
export const isOwnWorkClosingEnabled = isCurrentClient([Client.HENNEPIN]);

// Used in: WorkOrderStatusEdit.
export const isOtherOpenAssetWorkCheckEnabled = isCurrentClient([Client.GWRR]);

// Used in: TaskHeaderOptions, TaskListItem, TaskHeader, CollapsedTaskHeader.
export const isManagerRequiredForTaskCompletion = isCurrentTenant([
  Tenant.GWRR,
]);

// Used in: TaskHeaderOptions.
export const isTaskTypeSelectionEnabled = !isCurrentClient([Client.UDOT]);

// Only restricts editing when work date/time preferences are enabled.
// Used in: workPreferenceUtilities.
export const isWorkDateEditingRestricted = isCurrentClient([Client.OCP]);

// Used in: MainCreationModalView, WorkTemplateCreationModal.
export const isWorkTemplateInheritanceDisabled = isCurrentClient([
  Client.ALDOT,
]);

// Used in: WorkOrderDocuments, PhotosHeader, WorkOrderPhotos, WorkOrderVideos.
export const isManagerRequiredForWorkMediaEditing = isCurrentClient([
  Client.MACOMB,
]);

// Used in: ReportModal, reportOptions.
export const isTemplateReportSelectionEnabled = isCurrentClient([Client.HIDOT]);

// ── Task locations and materials ──

// Uses local multi-location emulation.
// Used in: TaskLocationSidebar, taskLocationUtilities,
// WorkOrdersFiltersLocationSection.
export const taskLocationClient = getCurrentClient(MAL_EMULATION_CLIENT);

// Used in: WorkOrdersFilters.
export const isWorkLocationFilteringEnabled = isCurrentClient([
  Client.ALDOT,
  Client.UDOT,
]);

// ALDOT task asset attributes, location constraints, and help text.
// Used in: TaskAssetRow, TaskAssetModal, TaskLocationModal, taskLocationUtilities,
// TaskAssetSidebar, TaskLocationSidebar.
export const isAldotTaskLocationEnabled = isCurrentClient([Client.ALDOT]);

// Used in: MaterialModal.
export const isHighestMaterialReadingEnabled = isCurrentClient([
  Client.ALDOT,
  Client.ARDOT,
  Client.MDOT,
]);

// End-joint/wearing-surface options instead of defect entry.
// Used in: FhwaFieldRow.
export const isCbitFhwaEnabled = isCurrentClient([Client.CBIT]);

// ── Inventory ──

// Used in: CreateControls, InventoryHeader.
export const isInspectorAssetCreateRoleEnabled = isCurrentTenant([Tenant.UDOT]);

// Used in: CreateControls, InventoryHeader.
export const isOrgAdminRequiredForInventoryCreate = isCurrentClient([
  Client.UDOT_TMD,
  Client.ARDOT,
]);

// Used in: WorkOrderAssetView, useSubItemTree (inventory sub items).
export const isOrgAdminRequiredForAssetAddition = isCurrentClient([
  Client.ARDOT,
]);

// Used in: InventoryPortal; HeaderControls (inventory); InventoryResultRow.
export const isManagerInventoryMoveRoleEnabled = isCurrentClient([
  Client.HENNEPIN,
]);

// Used in: InventoryCategoryTreeOptions.
export const isAdminRequiredForInventoryFolderEditing = isCurrentClient([
  Client.ARDOT,
]);

// Used in: EditAssetModal (inventory).
export const isAssetBudgetEditingDisabled = isCurrentClient([
  Client.UDOT,
  Client.ALDOT,
]);

// Used in: EditAssetModal (inventory), the asset request flags below, and
// ADOT request components.
export const isAdotAssetRequestsEnabled =
  isCurrentClient([Client.ADOT]) && showAdotInventoryRequests;

// ADOT locks direct stock editing only where its inventory requests are live.
// Add Client.ADOT to the list when showAdotInventoryRequests is removed.
// Used in: EditAssetModal (inventory).
export const isAdminRequiredForAssetQuantityEditing =
  isCurrentClient([Client.SDDOT]) || isAdotAssetRequestsEnabled;

// Used in: InventoryExportSidebar.
export const isHennepinInventoryExportEnabled = isCurrentClient([
  Client.HENNEPIN,
]);

// Used in: policyUtilities.
export const isInventoryReviewReceiveEnabled = isCurrentClient([Client.ARDOT]);

// ── Asset requests ──

// Uses local request emulation.
// Used in: customTenantUtilities.
export const assetRequestClient = getCurrentClient(
  ASSET_REQUEST_EMULATION_CLIENT,
);

// Add Client.ADOT to the list when showAdotInventoryRequests is removed.
// Used in: InventoryHeader.
export const isAssetRequestsEnabled =
  isCurrentClient([Client.SDDOT, Client.ARDOT]) || isAdotAssetRequestsEnabled;

// Add Client.ADOT to the list when showAdotInventoryRequests is removed.
// Used in: InventoryDetailHeader, getViews (inventory).
export const isStockBasedAssetRequestsEnabled =
  isCurrentClient([Client.SDDOT]) || isAdotAssetRequestsEnabled;

// Used in: InventoryDetailHeader, getViews (inventory).
export const isMaterialAssetRequestsEnabled = isCurrentClient([Client.ARDOT]);

// Explicitly excludes local, unlike the portal filter.
// Used in: CreateRequestModal, RequestsFilters (inventory).
export const isAssetRequestTypeFilterDisabled = isCurrentClient(
  [Client.ARDOT],
  true,
);

// Used in: RequestsFilters (requests portal).
// Follows the asset request emulation client so local matches the emulated tenant.
export const isArdotPortalRequestFilteringEnabled =
  assetRequestClient === Client.ARDOT;

// Used in: RequestsHeader (inventory).
// Follows the asset request emulation client so local matches the emulated tenant.
export const isPendingRequestCheckEnabled = assetRequestClient === Client.ARDOT;

// Add Client.ADOT to the list when showAdotInventoryRequests is removed.
// Used in: CreateRequestModal (inventory).
export const isAssetRefreshOnRequestCreationEnabled =
  isCurrentClient([Client.SDDOT]) || isAdotAssetRequestsEnabled;

// Add Client.ADOT to the list when showAdotInventoryRequests is removed.
// Used in: ViewRequestModal.
export const isAssetRefreshOnRequestApprovalEnabled =
  isCurrentClient([Client.ARDOT]) || isAdotAssetRequestsEnabled;

// ── Users and budgets ──

// Used in: InfoSection (user detail), UserInfoTab (map).
export const isEmployeeIdMaskingEnabled = isCurrentClient([Client.ALDOT]);

// Used in: InfoSection.
export const isEmployeeProfileEditingDisabled = isCurrentClient([
  Client.ALDOT,
  Client.ARDOT,
]);

// Used in: BudgetsTab.
export const isUserBudgetCreationEnabled = !isCurrentTenant([
  Tenant.UDOT,
  Tenant.UAT_UDOT,
]);

// Used in: BudgetDetailHeader, BudgetsHeader, BudgetsNoneAvailable, BudgetsTable.
export const isRestrictedBudgetRolesEnabled = isCurrentEnvironment([
  Environment.QA,
  Environment.PRODUCTION,
]);

// Applies when restricted budget roles are enabled.
// Used in: BudgetsTable.
export const isOrgAdminBudgetDuplicateRoleEnabled = isCurrentClient([
  Client.UDOT,
]);
